import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { SURFACE_COLORS, DefaultTextColorStyle } from 'Style/colors';
// Utilities
import StoryboardRenderingUtil from 'Utils/content/storyboard-rendering-util';
import classNames from 'classnames';
import { SPACING } from 'Style/spacing';
import { BREAKPOINTS } from 'Style/breakpoints';
import { CONTENT_BODY } from 'Style/typography';
import { overflowWordBreak } from 'Style/definitions';
// Components
import StoryboardPostPreview from './post-preview';
import StoryboardPostText, { StoryboardPostExcerpt } from './post-text';
import ImageUnavailable from '../../components/image-unavailable';
import SectionItemDisplay from 'Webapp/shared/app/containers/section-item-display';
import MeatballsMenuIcon from 'ComponentLibrary/icons/meatballs-menu';
import TooltipMenu from 'ComponentLibrary/menus/tooltip-menu';
import MeatballItemActions from '../../components/meatball-item-actions';

import connector from 'Utils/connector';
import connectAmp from 'Webapp/shared/app/connectors/connectAmp';

import { BUTTON_SETS } from 'Webapp/shared/app/hocs/withItemActions';

const { sizes } = StoryboardRenderingUtil;

const PREVIEW_HEIGHT = '232px';
const PREVIEW_HEIGHT_LARGE = '282px';

// Only applied for small and medium posts
const STORYBOARD_POST_BASE = {
  flexDirection: 'column',

  '.storyboard-post__preview': {
    height: 'initial',
  },

  '.storyboard-post__media-wrapper': {
    width: 'initial',
  },

  '.storyboard-post__media-wrapper:after': {
    content: '""',
    position: 'absolute',
    top: '100%',
    left: '29px',
    borderTop: `10px solid ${SURFACE_COLORS.secondary}`,
    borderTopColor: SURFACE_COLORS.secondary,
    borderRight: '13px solid transparent',
  },

  '.storyboard-post__content': {
    marginBottom: SPACING.MEDIUM,
    width: '100%',
  },
};

const STORYBOARD_POST_SMALL = {
  display: 'grid',
  gridTemplateColumns: '80px auto',
  gridColumnGap: '12px',
  '.storyboard-post__preview ': {
    height: '80px',
    '.image-frame': {
      width: '80px',
      height: '80px',
    },
  },
  '.storyboard-post__media-wrapper': {
    width: '80px',
    height: '80px',
  },
  '.image-unavailable__avatar': {
    height: '60px',
    width: '60px',
  },
  '&.storyboard-post--status': {
    ...STORYBOARD_POST_BASE,
    display: 'flex',
    alignItems: 'stretch',

    '.storyboard-post__preview': {
      marginRight: 'initial',
      height: 'initial',
    },

    '.storyboard-post__media-wrapper': {
      height: '100%',
      width: 'initial',
    },

    '.storyboard-post__content': {
      marginTop: SPACING.MEDIUM,
      flexGrow: 'initial',
      marginBottom: SPACING.MEDIUM,
      width: '100%',
    },
  },
};

const STORYBOARD_POST_MEDIUM = {
  flexBasis: '50%',
  flexDirection: 'column',
  alignItems: 'stretch',
  '&.storyboard-post--status': {
    ...STORYBOARD_POST_BASE,
    '.storyboard-post__media-wrapper': {
      height: 'initial',
      minHeight: PREVIEW_HEIGHT,
      width: 'initial',
    },
  },

  '.storyboard-post__preview': {
    flexShrink: '0',
    marginBottom: SPACING.MEDIUM,
    minHeight: PREVIEW_HEIGHT,
    height: 'initial',

    '.image-frame': {
      width: '100%',
      height: PREVIEW_HEIGHT,
    },
  },

  '.storyboard-post__media-wrapper': {
    width: '100%',
    height: PREVIEW_HEIGHT,
  },

  '.storyboard-post__content': {
    width: '100%',
    marginBottom: SPACING.MEDIUM,
  },
};

const STORYBOARD_POST_LARGE = {
  '.storyboard-post__preview': {
    flexShrink: '0',

    '.image-frame, .storyboard-post__media-wrapper': {
      width: '452px',
    },

    '.image-frame': {
      height: PREVIEW_HEIGHT_LARGE,
    },

    '.storyboard-post__media-wrapper': {
      margin: '0 auto',
      height: PREVIEW_HEIGHT_LARGE,
      width: '452px',
    },

    '&:not(:last-child)': {
      marginRight: SPACING.XLARGE,
    },
  },

  '.storyboard-post__content': {
    flexGrow: '1',
  },
  ...BREAKPOINTS.tabletPortraitDown({
    flexDirection: 'column',

    '.storyboard-post__preview': {
      marginBottom: SPACING.MEDIUM,

      '.image-frame,.storyboard-post__media-wrapper': {
        width: '100%',
      },

      '&:not(:last-child)': {
        marginRight: 'initial',
      },
    },

    ' .storyboard-post__content': {
      width: '100%',
    },
  }),

  '&.storyboard-post--status': {
    '.storyboard-post__media-wrapper': {
      height: 'initial',
      minHeight: PREVIEW_HEIGHT_LARGE,
    },
  },
};

const StyledArticle = styled.article(
  {
    minWidth: '0',
    position: 'relative',
    display: 'flex',
    margin: `${SPACING.XLARGE} 0`,
    [StoryboardPostExcerpt]: CONTENT_BODY,
    ...BREAKPOINTS.tabletPortraitUp({
      // Grid takes over from here on .storyboard-group, applying grid gaps in place of margins
      margin: '0',
    }),
  },
  (props) => ({
    ...props?.storyboardPostSize,
    [StoryboardPostExcerpt]: DefaultTextColorStyle(!props.isInline),
  }),
);

const StyledStoryboardPostContent = styled.div({
  ...overflowWordBreak,
  wordBreak: 'break-word',
  display: 'grid',
  gridTemplateColumns: 'minmax(218px,1fr) auto',
});

const StyledStoryboardPostActions = styled.div({
  display: 'flex',
  alignItems: 'start',
  marginLeft: SPACING.MEDIUM,
  flexShrink: '0',
});

const StyledStoryboardPostActionsIcon = styled(MeatballsMenuIcon)({
  padding: `${SPACING.BASE} ${SPACING.MEDIUM}`,
});

const getStoryboardPostSize = (size) => {
  if (size === sizes.SMALL) {
    return STORYBOARD_POST_SMALL;
  } else if (size === sizes.MEDIUM) {
    return STORYBOARD_POST_MEDIUM;
  } else if (size === sizes.LARGE) {
    return STORYBOARD_POST_LARGE;
  }
  return {};
};
function StoryboardPost({
  item,
  section,
  group,
  index,
  isAmp,
  postStyleOverride,
  isInline,
}) {
  const size = StoryboardRenderingUtil.groupSize(group);

  const storyboardPostSize = getStoryboardPostSize(size);

  const className = classNames({
    'storyboard-post--small': size === sizes.SMALL,
    'storyboard-post--medium': size === sizes.MEDIUM,
    'storyboard-post--large': size === sizes.LARGE,
    'storyboard-post--status': item.isStatus,
    'storyboard-post--text': item.isStatus,
    'storyboard-post--twitter': item.isTwitter,
    'storyboard-post--section': item.isSection,
  });

  let fallbackElementSize;
  switch (size) {
    case sizes.SMALL:
      {
        fallbackElementSize = ImageUnavailable.fallbackElementSizes.CONDENSED;
      }
      break;
    default: {
      fallbackElementSize = ImageUnavailable.fallbackElementSizes.CARD;
    }
  }

  return (
    <SectionItemDisplay item={item} postStyleOverride={postStyleOverride}>
      <StyledArticle
        storyboardPostSize={storyboardPostSize}
        isInline={isInline}
        className={className}
      >
        <StoryboardPostPreview
          item={item}
          section={section}
          group={group}
          index={index}
          key={`post-preview-${index}`}
          fallbackElementSize={fallbackElementSize}
        />
        <StyledStoryboardPostContent className="storyboard-post__content">
          <StoryboardPostText
            item={item}
            section={section}
            group={group}
            index={index}
            isInline={isInline}
          />
          {!isAmp && !item.isStatus && (
            <StyledStoryboardPostActions className="storyboard-post__actions">
              <TooltipMenu
                toggleElement={
                  <StyledStoryboardPostActionsIcon className="storyboard-post__actions-icon" />
                }
              >
                <MeatballItemActions
                  item={item}
                  section={section}
                  isTooltipMenu
                  buttonSet={BUTTON_SETS.STORYBOARD_POST}
                />
              </TooltipMenu>
            </StyledStoryboardPostActions>
          )}
        </StyledStoryboardPostContent>
      </StyledArticle>
    </SectionItemDisplay>
  );
}

StoryboardPost.propTypes = {
  item: PropTypes.object.isRequired,
  section: PropTypes.object.isRequired,
  group: PropTypes.object,
  index: PropTypes.number,
  isAmp: PropTypes.bool,
  postStyleOverride: PropTypes.object,
  isInline: PropTypes.bool,
};

StoryboardPost.defaultProps = {
  index: null,
  group: null,
  isAmp: false,
};

export default connector(connectAmp)(StoryboardPost);
